import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToast } from '@/utils/toast';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  Updater
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Trash, ChevronUp, ChevronDown, ChevronsUpDown, Plus, MapPin } from 'lucide-react';
import { ProviderDownload } from './ProviderDownload';
import { useProviders } from '@/hooks/useProviders';
import { useProviderStore } from '@/stores/providerStore';
import { ProviderWithType } from '../types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorDisplay } from '@/components/common/ErrorBoundary';
import { EmptyState } from '@/components/common/EmptyState';
import { formatDate } from '@/utils/formatting';
import { cn } from '@/lib/utils';
import { Checkbox } from "@/components/ui/checkbox";

export function ProviderTable() {
  const { data: providers, isLoading, error } = useProviders();
  const { 
    setSelectedProvider, 
    selectedProviderIds,
    toggleProviderSelection,
    setSelectedProviderIds,
    openCreateModal, 
    openDeleteModal,
    sortField,
    sortDirection,
  } = useProviderStore();

  // Add state for row selection
  const [rowSelection, setRowSelection] = useState({});

  // Track component mount
  useEffect(() => {
    // Track provider list view
    
    return () => {
      // Could track exit time if needed
    };
  }, []);

  // Reset row selection when selectedProviderIds changes
  useEffect(() => {
    if (selectedProviderIds.length === 0) {
      setRowSelection({});
    }
  }, [selectedProviderIds]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: sortField, desc: sortDirection === 'desc' }
  ]);

  const navigate = useNavigate();

  // Track navigation to provider versions
  const handleNavigateToVersions = (providerId: number) => {
    
    navigate(`/providers/${providerId}/versions`);
  };

  // Track navigation to provider regions
  const handleNavigateToRegions = (providerId: number, e?: React.MouseEvent) => {
    // Prevent event propagation if event exists
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!providerId) {
      console.error('Cannot navigate to regions: Missing provider ID');
      showToast.error('Cannot navigate to regions: Missing provider ID');
      return;
    }
    
    try {
      // Track provider regions navigation
      
      // Navigate with a short delay to ensure event is fully processed
      setTimeout(() => {
        navigate(`/providers/${providerId}/regions`);
      }, 0);
    } catch (error) {
      console.error('Error navigating to regions:', error);
      showToast.error('Error navigating to regions');
    }
  };

  // Column definitions
  const columns = useMemo<ColumnDef<ProviderWithType>[]>(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
            const selectedIds = value 
              ? table.getRowModel().rows.map(row => row.original.id)
              : [];
            setSelectedProviderIds(selectedIds);
            
            // Track bulk selection
            if (value && table.getRowModel().rows.length > 0) {
              
            }
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => {
            row.toggleSelected(!!value);
            toggleProviderSelection(row.original.id);
            
            // Track provider selection
            if (value) {
              
            }
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      cell: ({ row }) => {
        const provider = row.original;
        return (
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => handleNavigateToVersions(provider.id)}
          >
            {provider.logo_url && (
              <img
                src={provider.logo_url}
                alt={provider.name}
                className="w-8 h-8 rounded object-contain"
              />
            )}
            <span className="font-medium">{provider.name}</span>
          </div>
        );
      }
    },
    {
      id: 'type',
      header: 'Type',
      accessorFn: (row) => row.type?.name ?? 'N/A'
    },
    {
      id: 'info',
      header: 'Business Info',
      cell: ({ row }) => {
        const provider = row.original;
        return (
          <div className="space-y-1">
            {provider.website_url && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-blue-600 hover:underline">
                  <a href={provider.website_url} target="_blank" rel="noopener noreferrer">
                    Website
                  </a>
                </span>
              </div>
            )}
            {provider.free_trial_days && (
              <div className="text-xs text-green-600">
                {provider.free_trial_days} day trial
              </div>
            )}
            {provider.student_discount_available && (
              <div className="text-xs text-purple-600">
                Student discount
              </div>
            )}
            {provider.support_email && (
              <div className="text-xs text-gray-600">
                Support available
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'sequential',
      header: 'Sequential',
      accessorKey: 'has_sequence_numbers',
      cell: ({ row }) => {
        const isSequential = row.original.has_sequence_numbers;
        return (
          <div className="flex items-center">
            <span className={cn(
              "px-2 py-1 rounded-full text-xs font-medium",
              isSequential 
                ? "bg-green-100 text-green-700" 
                : "bg-gray-100 text-gray-700"
            )}>
              {isSequential ? 'Sequential' : 'Non-Sequential'}
            </span>
          </div>
        );
      }
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'created_at',
      cell: ({ getValue }) => formatDate(getValue() as string)
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const provider = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => handleNavigateToRegions(provider.id, e)}
            >
              <MapPin className="h-4 w-4 mr-2" />
              Regions
            </Button>
            <ProviderDownload 
              providerId={provider.id} 
              variant="outline" 
              size="sm"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => {
                    setSelectedProvider(provider);
                    openCreateModal();
                    
                    // Track edit action
                    
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-red-600"
                  onClick={() => {
                    if (provider.id) {
                      openDeleteModal();
                      setSelectedProvider(provider);
                      
                      // Track delete action
                      
                    }
                  }}
                >
                  <Trash className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      }
    }
  ], [navigate, setSelectedProvider, openDeleteModal, setSelectedProviderIds, toggleProviderSelection, handleNavigateToVersions, handleNavigateToRegions]);

  // Table instance
  const table = useReactTable({
    data: providers ?? [],
    columns,
    state: {
      sorting,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: useCallback((updaterOrValue: Updater<SortingState>) => {
      setSorting(updaterOrValue);
      
      // Note: Tracking was removed, but sorting functionality remains
      if (typeof updaterOrValue !== 'function') {
        // Direct value provided
        if (Array.isArray(updaterOrValue) && updaterOrValue.length > 0) {
          // Sorting logic can be added here if needed
        }
      }
    }, []),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  // Add bulk actions
  const BulkActions = () => {
    if (selectedProviderIds.length === 0) return null;

    return (
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-muted-foreground">
          {selectedProviderIds.length} selected
        </span>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            openDeleteModal();
            
            // Track bulk delete action
            
          }}
        >
          Delete Selected
        </Button>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (error) {
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'An error occurred loading providers';
    
    return <ErrorDisplay error={new Error(errorMessage)} />;
  }

  // Empty state
  if (!providers?.length) {
    return (
      <EmptyState
        title="No providers found"
        description="Get started by creating a new provider"
        action={
          <Button onClick={openCreateModal}>
            <Plus className="mr-2 h-4 w-4" />
            Add Provider
          </Button>
        }
      />
    );
  }

  // Table render
  return (
    <div className="space-y-4">
      <BulkActions />
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          "flex items-center gap-2",
                          header.column.getCanSort() && "cursor-pointer select-none"
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <SortIcon direction={header.column.getIsSorted()} />
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Helper component for sort icon
function SortIcon({ direction }: { direction: false | 'asc' | 'desc' }) {
  if (!direction) return <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />;
  return direction === 'asc' ? 
    <ChevronUp className="h-4 w-4" /> : 
    <ChevronDown className="h-4 w-4" />;
}
